import { useMemo } from 'react'
import {
  IconProps,
  IconSizes,
  StoreLocationConfig
} from '@dcxwam/dcx-wam-ui-foundations'
import { REPLACE_SEARCH_INPUT } from '../../../../../utils/constants'

interface UseNoStoresFoundConfigParams {
  isLoadingStores: boolean
  storeLocations: StoreLocationConfig[] | undefined
  initialStoreCount: number | null
  zoom: number
  noStoresMsg?: string
  noStoreTitle?: string
  nearStoresMsg?: string
  storeLandingPageButtonLabel?: string
  locationSearchQuery: string | null
}

interface NoStoresFoundConfig {
  noStoresMessage?: string
  noStoreTitle?: string
  iconConfig?: IconProps
  storeLandingPageButtonLabel?: string
}

const EMPTY_CONFIG: NoStoresFoundConfig = {}

const replaceSearchQuery = (
  message: string | undefined,
  query: string | null
): string | undefined => {
  if (!message) {
    return message
  }
  return message.replace(REPLACE_SEARCH_INPUT, query ?? '')
}

export const useNoStoresFoundConfig = (
  params: UseNoStoresFoundConfigParams
): NoStoresFoundConfig => {
  const {
    isLoadingStores,
    storeLocations,
    initialStoreCount,
    zoom,
    noStoresMsg,
    noStoreTitle,
    nearStoresMsg,
    storeLandingPageButtonLabel,
    locationSearchQuery
  } = params

  return useMemo(() => {
    if (isLoadingStores) {
      return EMPTY_CONFIG
    }

    const hasNoStores = !storeLocations || storeLocations.length === 0
    const isZoomedBeyondLimit = initialStoreCount === 0 && !hasNoStores

    if (hasNoStores) {
      const message = replaceSearchQuery(noStoresMsg, locationSearchQuery)
      return {
        noStoresMessage: message,
        iconConfig: {
          image: 'pmi-icon-store',
          size: IconSizes.Large
        },
        noStoreTitle,
        storeLandingPageButtonLabel
      }
    }

    if (isZoomedBeyondLimit) {
      const message = replaceSearchQuery(nearStoresMsg, locationSearchQuery)
      return {
        noStoresMessage: message
      }
    }

    return EMPTY_CONFIG
  }, [
    // Config values (radiusLimit, noStoresMsg, noStoreTitle, nearStoresMsg,
    // storeLandingPageButtonLabel) are from AEM dialog and don't change during lifecycle
    isLoadingStores,
    storeLocations,
    initialStoreCount,
    zoom,
    locationSearchQuery
  ])
}
